import type { WakewordGateStatus } from "./wakewordGate";
import type { VoicePermissionState } from "./voiceTrace";

export type OwnerTtsDecisionReason = "empty" | "ok" | "long_answer" | "sensitive_content";

export interface OwnerTtsDecision {
  shouldSpeak: boolean;
  text: string;
  reason: OwnerTtsDecisionReason;
  matchedSensitiveKinds: string[];
}

export interface WebVoiceOwnerUxInput {
  voiceMode: "off" | "server";
  voiceAvailable: boolean;
  permission: VoicePermissionState;
  recording: boolean;
  processing: boolean;
  speaking: boolean;
  selectedDeviceLabel: string;
  ttsVoiceLabel: string;
  wakewordGate: WakewordGateStatus;
  lastTrace: string | null;
}

export interface WebVoiceOwnerUxSummary {
  pttState: "off" | "unavailable" | "permission_needed" | "listening" | "processing" | "ready";
  pttLabel: string;
  ttsLabel: string;
  wakeLabel: string;
  contextLabel: string;
  boundaryBadges: string[];
}

const LONG_TTS_LIMIT = 520;

const SENSITIVE_PATTERNS: Array<{ kind: string; pattern: RegExp }> = [
  { kind: "password", pattern: /\b(passwort|password|pwd)\b\s*[:=]/i },
  { kind: "token", pattern: /\b(api[_ -]?key|token|secret|bearer|authorization)\b\s*[:=]/i },
  { kind: "private-key", pattern: /-----BEGIN (?:RSA |OPENSSH |EC |DSA )?PRIVATE KEY-----/i },
  { kind: "otp", pattern: /\b(otp|mfa|2fa|tan|recovery code|backup code)\b\s*[:=]?\s*[a-z0-9 -]{4,}/i },
  { kind: "connection-string", pattern: /\b(postgres|postgresql|mysql|mongodb|redis):\/\/[^\s]+/i },
  { kind: "credit-card", pattern: /\b(?:\d[ -]*?){13,19}\b/ },
  { kind: "iban", pattern: /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/i },
];

export function detectSensitiveSpeechKinds(text: string): string[] {
  const matches = new Set<string>();
  for (const item of SENSITIVE_PATTERNS) {
    if (item.pattern.test(text)) matches.add(item.kind);
  }
  return [...matches];
}

export function prepareOwnerTtsText(text: string): OwnerTtsDecision {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return { shouldSpeak: false, text: "", reason: "empty", matchedSensitiveKinds: [] };
  }
  const sensitiveKinds = detectSensitiveSpeechKinds(normalized);
  if (sensitiveKinds.length > 0) {
    return {
      shouldSpeak: false,
      text: "",
      reason: "sensitive_content",
      matchedSensitiveKinds: sensitiveKinds,
    };
  }
  if (normalized.length > LONG_TTS_LIMIT) {
    return {
      shouldSpeak: true,
      text: "Die Antwort ist laenger und steht im Chat. Ich lese sie nicht komplett vor.",
      reason: "long_answer",
      matchedSensitiveKinds: [],
    };
  }
  return { shouldSpeak: true, text: normalized, reason: "ok", matchedSensitiveKinds: [] };
}

export function deriveWebVoiceOwnerUxPolish(input: WebVoiceOwnerUxInput): WebVoiceOwnerUxSummary {
  let pttState: WebVoiceOwnerUxSummary["pttState"] = "ready";
  if (input.voiceMode === "off") pttState = "off";
  else if (!input.voiceAvailable) pttState = "unavailable";
  else if (input.permission === "denied" || input.permission === "prompt" || input.permission === "unknown") pttState = "permission_needed";
  else if (input.recording) pttState = "listening";
  else if (input.processing) pttState = "processing";

  const pttLabel: Record<WebVoiceOwnerUxSummary["pttState"], string> = {
    off: "PTT aus",
    unavailable: "Mic nicht verfuegbar",
    permission_needed: "Mic-Freigabe pruefen",
    listening: "Hoert zu",
    processing: "STT verarbeitet",
    ready: "PTT bereit",
  };

  const ttsLabel = input.speaking
    ? `TTS spricht · ${input.ttsVoiceLabel}`
    : `TTS ruhig · ${input.ttsVoiceLabel}`;
  const wakeLabel = input.wakewordGate.active
    ? "Wake aktiv"
    : input.wakewordGate.canRequest
      ? `Wake fail-closed · ${input.wakewordGate.reason}`
      : "Wake build-gated";
  const contextLabel = [
    input.selectedDeviceLabel || "Standard-Mic",
    input.lastTrace ? input.lastTrace : null,
  ].filter(Boolean).join(" · ");

  return {
    pttState,
    pttLabel: pttLabel[pttState],
    ttsLabel,
    wakeLabel,
    contextLabel,
    boundaryBadges: [
      "push-to-talk",
      "tts-sensitive-guard",
      "wakeword-fail-closed",
      "no-new-write-capability",
    ],
  };
}
